import { getAdsFromStorage } from 'analytics/ads';
import { useEffect } from 'react';

let currentTrackingId = null;
let currentEventId = null;
let isReady = false;

function loadGAScript(trackingId, onReady) {
    const existingScript = document.querySelector(
        `script[src="https://www.googletagmanager.com/gtag/js?id=${trackingId}"]`,
    );
    if (existingScript) {
        onReady();
        return;
    }

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    script.async = true;
    script.onload = onReady;
    document.head.appendChild(script);
}

export const AnalyticsManager = {
    init(trackingId, eventId) {
        if (!trackingId || trackingId === currentTrackingId) return;

        // Disable previous GA container
        if (currentTrackingId) {
            window[`ga-disable-${currentTrackingId}`] = true;
        }

        // Set new active ID
        currentTrackingId = trackingId;
        currentEventId = eventId;
        window[`ga-disable-${trackingId}`] = false;

        loadGAScript(trackingId, () => {
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                window.dataLayer.push(arguments);
            }
            window.gtag = window.gtag || gtag;

            window.gtag('js', new Date());
            window.gtag('config', trackingId);
            isReady = true;
        });
    },

    event(name, params = {}) {
        if (isReady && typeof window.gtag === 'function') {
            const { fbclid, gclid } = getAdsFromStorage(currentEventId);

            window.gtag('event', name, {
                ...params,
                ...(gclid && { gclid }),
                ...(fbclid && { fbclid }),
            });
        } else {
            console.warn('[GA] Not ready, event skipped:', name);
        }
    },

    pageView(path, title) {
        if (isReady && typeof window.gtag === 'function') {
            const { fbclid, gclid } = getAdsFromStorage(currentEventId);
            window.gtag('event', 'page_view', {
                page_path: path,
                page_title: title,
                page_location: window.location.href,
                ...(gclid && { gclid }),
                ...(fbclid && { fbclid }),
            });
        }
    },

    disable(trackingId) {
        if (trackingId) {
            const existingScript = document.querySelector(
                `script[src="https://www.googletagmanager.com/gtag/js?id=${trackingId}"]`,
            );

            existingScript?.remove();

            window[`ga-disable-${trackingId}`] = true;

            if (trackingId === currentTrackingId) {
                currentTrackingId = null;
                currentEventId = null;
                isReady = false;
            }
        }
    },
};

class GoogleAnalytics {
    pageView(params) {
        if (process.env.REACT_APP_ENV === 'development') {
        }

        AnalyticsManager.pageView(params.path, params.title);
    }

    event(name, params) {
        if (process.env.REACT_APP_ENV === 'development') {
        }
        AnalyticsManager.event(name, params);
    }

    addToCart(productId, productName, quantity, value, currency = 'CAD') {
        AnalyticsManager.event('add_to_cart', {
            currency,
            value,
            items: [
                {
                    item_id: productId,
                    item_name: productName,
                    quantity,
                    price: value,
                },
            ],
        });
    }

    beginCheckout(value, items = [], coupon, currency = 'CAD') {
        AnalyticsManager.event('begin_checkout', {
            currency,
            value,
            items,
            coupon,
        });
    }

    purchaseCompleted(transaction_id, value, tax, coupon, items = [], currency = 'CAD') {
        AnalyticsManager.event('purchase', {
            transaction_id,
            currency,
            value,
            tax,
            coupon,
            items,
        });
    }
}

export const googleAnalytics = new GoogleAnalytics();

export function useGoogleAnalytics(trackingId, eventId) {
    useEffect(() => {
        if (trackingId) {
            AnalyticsManager.init(trackingId, eventId);
        }

        return () => {
            if (trackingId) {
                AnalyticsManager.disable(trackingId);
            }
        };
    }, [trackingId, eventId]);
}
